import {
  Document,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  UploadRequest,
  UploadResponse,
  KnowledgeBaseStats,
  ChatSession,
  DocumentStatus,
} from "../types/knowledge";
import { API_URL, ENDPOINTS } from "../../../foundation/altzero_base/base/utils/constants";
import {
  KNOWLEDGE_ENDPOINTS,
  KNOWLEDGE_TOAST_MESSAGES,
} from "../utils/constants";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

class KnowledgeService {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = API_URL;
    this.apiKey = import.meta.env.VITE_API_KEY || "";
    console.log(`🔍 DEBUG: KnowledgeService initialized with baseUrl: ${this.baseUrl}`);
  }


  // Get current user ID from Supabase session
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.warn("Failed to get Supabase session:", error);
        return null;
      }

      return session?.user?.id || null;
    } catch (error) {
      console.warn("Failed to get user ID from Supabase:", error);
      return null;
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const userId = await this.getCurrentUserId();

    const defaultHeaders: Record<string, string> = {
      "x-api-key": this.apiKey,
    };

    // Add user ID to headers if available
    if (userId) {
      defaultHeaders["x-user-id"] = userId;
    }

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      defaultHeaders["Content-Type"] = "application/json";
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    // Add userId to request body for POST/PUT requests if not FormData
    if (
      userId &&
      (options.method === "POST" || options.method === "PUT") &&
      !(options.body instanceof FormData) &&
      options.body
    ) {
      try {
        const bodyData = JSON.parse(options.body as string);
        bodyData.userId = userId;
        config.body = JSON.stringify(bodyData);
      } catch (error) {
        // If body is not JSON, just continue
      }
    }

    console.log(`🔍 DEBUG: Making request to: ${url}`);
    console.log(`🔍 DEBUG: Base URL: ${this.baseUrl}`);
    console.log(`🔍 DEBUG: Endpoint: ${endpoint}`);

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ API request failed for ${endpoint}:`, error);
      console.error(`❌ Full URL: ${url}`);
      console.error(`❌ Error type: ${error.constructor.name}`);
      console.error(`❌ Error message: ${error.message}`);

      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error(
          `Cannot connect to backend server at ${this.baseUrl}. ` +
          `Please ensure the backend server is running and accessible.`
        );
      }

      throw error;
    }
  }

  // Helper method to split file into chunks
  private splitFileIntoChunks(file: File, chunkSize: number = 1024 * 1024): Blob[] {
    const chunks: Blob[] = [];
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      chunks.push(file.slice(start, end));
      start = end;
    }

    return chunks;
  }

  // Helper method to upload a single file with chunking
  private async uploadSingleFileChunked(
    file: File,
    metadata: any,
    onProgress?: (progress: number, status: string, message: string) => void
  ): Promise<any> {
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const MAX_RETRIES = 3;
    const CHUNK_TIMEOUT = 30000; // 30 seconds per chunk
    const INITIATE_TIMEOUT = 10000; // 10 seconds for initiation
    const COMPLETE_TIMEOUT = 60000; // 60 seconds for completion

    // For small files (< 10MB), use regular upload
    if (file.size < 10 * 1024 * 1024) {
      return this.uploadSingleFileRegular(file, metadata, onProgress);
    }

    const chunks = this.splitFileIntoChunks(file, CHUNK_SIZE);
    const totalChunks = chunks.length;

    console.log(`📦 Uploading ${file.name} in ${totalChunks} chunks (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    // Step 1: Initiate chunked upload with timeout
    const initiateResponse = await Promise.race([
      this.makeRequest<{ sessionId: string }>(
        `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/initiate`,
        {
          method: "POST",
          body: JSON.stringify({
            fileName: file.name,
            fileSize: file.size,
            totalChunks: totalChunks,
            metadata: metadata
          })
        }
      ),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Upload initiation timeout")), INITIATE_TIMEOUT)
      )
    ]);

    const sessionId = initiateResponse.sessionId;
    console.log(`🚀 Initiated upload session: ${sessionId}`);

    // Step 2: Upload chunks with retry logic
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      let retryCount = 0;
      let chunkUploaded = false;

      while (!chunkUploaded && retryCount < MAX_RETRIES) {
        try {
          const chunkFormData = new FormData();
          chunkFormData.append("chunk", chunk);
          chunkFormData.append("sessionId", sessionId);
          chunkFormData.append("chunkIndex", chunkIndex.toString());

          const chunkResponse = await Promise.race([
            fetch(
              `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/chunk`,
              {
                method: "POST",
                headers: {
                  "x-api-key": this.apiKey,
                  "x-user-id": await this.getCurrentUserId() || "",
                },
                body: chunkFormData,
              }
            ),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("Chunk upload timeout")), CHUNK_TIMEOUT)
            )
          ]);

          if (!chunkResponse.ok) {
            throw new Error(`Chunk upload failed: ${chunkResponse.statusText}`);
          }

          const chunkResult = await chunkResponse.json();
          chunkUploaded = true;

          // Report real progress
          const progress = Math.round((chunkResult.totalUploaded / totalChunks) * 80); // 0-80% for upload
          onProgress?.(progress, "uploading", `Uploading ${file.name}... (${progress}%)`);

          console.log(`📦 Uploaded chunk ${chunkIndex + 1}/${totalChunks} for ${file.name} (${progress}%)`);

        } catch (error) {
          retryCount++;
          console.warn(`⚠️ Chunk ${chunkIndex} upload failed (attempt ${retryCount}/${MAX_RETRIES}):`, error);

          if (retryCount >= MAX_RETRIES) {
            throw new Error(`Failed to upload chunk ${chunkIndex} after ${MAX_RETRIES} attempts`);
          }

          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }
    }

    // Step 3: Complete upload and process file with timeout
    onProgress?.(85, "processing", `Processing ${file.name}...`);

    const completeResponse = await Promise.race([
      this.makeRequest<any>(
        `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/complete`,
        {
          method: "POST",
          body: JSON.stringify({ sessionId })
        }
      ),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Upload completion timeout")), COMPLETE_TIMEOUT)
      )
    ]);

    onProgress?.(100, "success", `${file.name} completed successfully`);
    return completeResponse;
  }

  // Helper method for regular upload (small files)
  private async uploadSingleFileRegular(
    file: File,
    metadata: any,
    onProgress?: (progress: number, status: string, message: string) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("metadata", JSON.stringify(metadata));
    formData.append("userId", await this.getCurrentUserId() || "");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 80);
          onProgress?.(progress, "uploading", `Uploading ${file.name}... (${progress}%)`);
        }
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            // Simulate processing progress (80% to 100%)
            for (let progress = 80; progress <= 100; progress += 10) {
              const status = progress < 100 ? "processing" : "success";
              const message = progress < 100
                ? `Processing ${file.name}... (${progress}%)`
                : `${file.name} completed successfully`;

              onProgress?.(progress, status, message);

              if (progress < 100) {
                await new Promise(resolve => setTimeout(resolve, 200));
              }
            }

            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error("Failed to parse response"));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("POST", `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload`);
      xhr.setRequestHeader("x-api-key", this.apiKey);
      xhr.setRequestHeader("x-user-id", metadata.userId);
      xhr.send(formData);
    });
  }

  // Document Management with LlamaParser → Pinecone workflow (now with chunked upload support)
  async uploadDocuments(
    files: File[],
    onProgress?: (progressData: { [key: string]: any }) => void,
    additionalMetadata?: {
      companyId?: string;
      aiExtractionInstructions?: string;
      category?: string;
      subCategory?: string;
      organisationId?: string;
      customTags?: string[];
    }
  ): Promise<UploadResponse> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required for document upload. Please log in."
      );
    }

    // Prepare metadata
    const metadata = {
      uploadedAt: new Date().toISOString(),
      source: "web_upload",
      userId: userId,
      ...(additionalMetadata?.companyId && {
        companyId: additionalMetadata.companyId,
      }),
      ...(additionalMetadata?.aiExtractionInstructions && {
        aiExtractionInstructions: additionalMetadata.aiExtractionInstructions,
      }),
      ...(additionalMetadata?.category && {
        category: additionalMetadata.category,
      }),
      ...(additionalMetadata?.subCategory && {
        subCategory: additionalMetadata.subCategory,
      }),
       ...(additionalMetadata?.organisationId && {
        organisationId: additionalMetadata.organisationId,
      }),
      ...(additionalMetadata?.customTags && {
        customTags: additionalMetadata.customTags,
      }),
    };

    console.log("Meta data ", JSON.stringify(metadata));

    // Generate consistent document IDs for progress tracking
    const uploadTimestamp = Date.now();
    const documentIds = files.map(
      (file, index) => `upload-${index}-${uploadTimestamp}`
    );

    const uploadedDocuments = [];
    const errors = [];

    // Upload files one by one with chunked upload support
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const documentId = documentIds[index];

      try {
        // Create progress callback for individual file
        const fileProgressCallback = onProgress ? (progress: number, status: string, message: string) => {
          const progressData: { [key: string]: any } = {};

          // Update progress for current file
          progressData[documentId] = {
            progress,
            status,
            message,
            fileName: file.name,
          };

          // Keep other files' last known status
          files.forEach((f, i) => {
            if (i !== index) {
              const otherId = documentIds[i];
              if (i < index) {
                // Already completed
                progressData[otherId] = {
                  progress: 100,
                  status: "success",
                  message: `${f.name} completed successfully`,
                  fileName: f.name,
                };
              } else {
                // Not started yet
                progressData[otherId] = {
                  progress: 0,
                  status: "pending",
                  message: `Waiting to upload ${f.name}...`,
                  fileName: f.name,
                };
              }
            }
          });

          onProgress(progressData);
        } : undefined;

        // Upload single file with chunking support
        const result = await this.uploadSingleFileChunked(file, metadata, fileProgressCallback);
        uploadedDocuments.push(result);

      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        errors.push({
          fileName: file.name,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        // Update progress to show error
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 0,
            status: "error",
            message: `Failed to upload ${file.name}`,
            fileName: file.name,
          };
          onProgress(progressData);
        }
      }
    }

    return {
      documents: uploadedDocuments,
      errors,
    };
  }

  // Search documents using Pinecone + LangGraph
  async searchDocuments(
    query: string,
    documentIds: string[] = [],
    options: {
      maxResults?: number;
      minRelevanceScore?: number;
    } = {}
  ): Promise<{
    results: Array<{
      id: string;
      text: string;
      score: number;
      metadata?: Record<string, any>;
    }>;
    sources: any[];
    totalResults: number;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required for document search. Please log in."
      );
    }

    return this.makeRequest<{
      results: Array<{
        id: string;
        text: string;
        score: number;
        metadata?: Record<string, any>;
      }>;
      sources: any[];
      totalResults: number;
    }>(`${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_SEARCH}`, {
      method: "POST",
      body: JSON.stringify({
        query,
        documentIds,
        userId: userId,
        ...options,
      }),
    });
  }

  // Chat with Pinecone RAG
  async chatWithPineconeRAG(request: ChatRequest): Promise<ChatResponse> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required for chat. Please log in.");
    }

    console.log(`🔍 FRONTEND - chatWithPineconeRAG called with:`);
    console.log(`   Message: "${request.message}"`);
    console.log(`   SelectedDocuments:`, request.selectedDocuments);
    console.log(`   SelectedDocuments type:`, typeof request.selectedDocuments);
    console.log(
      `   SelectedDocuments length:`,
      request.selectedDocuments?.length
    );
    console.log(
      `   SelectedDocuments array:`,
      Array.isArray(request.selectedDocuments)
    );
    if (request.selectedDocuments && request.selectedDocuments.length > 0) {
      console.log(
        `   SelectedDocuments values:`,
        request.selectedDocuments.map((id) => `"${id}"`)
      );
    }
    console.log(`   UserId: ${userId}`);

    return this.makeRequest<ChatResponse>(KNOWLEDGE_ENDPOINTS.CHAT, {
      method: "POST",
      body: JSON.stringify({
        ...request,
        userId: userId,
      }),
    });
  }

  // Document Management
  async getDocuments(companyId?: string, includeDefault?: boolean): Promise<Document[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required to get documents. Please log in.");
    }

    let url = `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${userId}`;
    if (companyId) {
      url += `&companyId=${encodeURIComponent(companyId)}`;
      if (includeDefault) {
        url += `&includeDefault=true`;
      }
    }

    return this.makeRequest<Document[]>(url);
  }

  // Get ALL documents (for client-side filtering)
  async getAllDocuments(): Promise<Document[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required to get documents. Please log in.");
    }

    const url = `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${userId}&all=true`;
    return this.makeRequest<Document[]>(url);
  }

  // Delete document
  async deleteDocument(documentId: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required to delete document. Please log in.");
    }

    return this.makeRequest<void>(
      `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/${documentId}?userId=${userId}`,
      {
        method: "DELETE",
      }
    );
  }

  // Bulk delete documents
  async deleteDocuments(documentIds: string[]): Promise<{
    deletedCount: number;
    deletedDocuments: Array<{ id: string; name: string }>;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to delete documents. Please log in."
      );
    }

    if (!documentIds || documentIds.length === 0) {
      throw new Error(
        "At least one document ID is required for bulk deletion."
      );
    }

    return this.makeRequest<{
      deletedCount: number;
      deletedDocuments: Array<{ id: string; name: string }>;
    }>(`${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${userId}`, {
      method: "DELETE",
      body: JSON.stringify({ documentIds }),
    });
  }

  // Chat Management
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required for chat. Please log in.");
    }

    return this.makeRequest<ChatResponse>(KNOWLEDGE_ENDPOINTS.CHAT, {
      method: "POST",
      body: JSON.stringify({
        ...request,
        userId: userId,
      }),
    });
  }

  async streamChatMessage(
    request: ChatRequest,
    onChunk: (chunk: string) => void,
    onComplete: (response: ChatResponse) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      onError(
        new Error("User ID is required for chat streaming. Please log in.")
      );
      return;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.CHAT_STREAM}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": this.apiKey,
            "x-user-id": userId,
          },
          body: JSON.stringify({
            ...request,
            userId: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Response body is not readable");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);

            if (data === "[DONE]") {
              return;
            }

            try {
              const chunk = JSON.parse(data);

              if (chunk.error) {
                onError(new Error(chunk.error));
                return;
              }

              if (chunk.chunk) {
                fullResponse += chunk.chunk;
                onChunk(chunk.chunk);
              }

              if (chunk.complete) {
                onComplete({
                  message: chunk.message || fullResponse,
                  sources: chunk.sources || [],
                  sessionId: request.sessionId || "",
                  metadata: chunk.metadata || {},
                });
                return;
              }
            } catch (e) {
              // Ignore malformed chunks
            }
          }
        }
      }
    } catch (error) {
      onError(
        error instanceof Error ? error : new Error("Unknown streaming error")
      );
    }
  }

  async getChatSessions(): Promise<ChatSession[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to get chat sessions. Please log in."
      );
    }

    return this.makeRequest<ChatSession[]>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}?userId=${userId}`
    );
  }

  async createChatSession(
    title: string,
    selectedDocuments: string[]
  ): Promise<ChatSession> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to create chat session. Please log in."
      );
    }

    return this.makeRequest<ChatSession>(KNOWLEDGE_ENDPOINTS.CHAT_HISTORY, {
      method: "POST",
      body: JSON.stringify({
        title,
        selectedDocuments,
        userId: userId,
      }),
    });
  }

  async updateChatSession(
    sessionId: string,
    updates: Partial<ChatSession>
  ): Promise<ChatSession> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to update chat session. Please log in."
      );
    }

    return this.makeRequest<ChatSession>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}/${sessionId}?userId=${userId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          ...updates,
          userId: userId,
        }),
      }
    );
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to delete chat session. Please log in."
      );
    }

    return this.makeRequest<void>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}/${sessionId}?userId=${userId}`,
      {
        method: "DELETE",
      }
    );
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  getFileIcon(fileType: string): string {
    const type = fileType.toLowerCase();

    if (type.includes("pdf")) return "📄";
    if (type.includes("word") || type.includes("doc")) return "📝";
    if (type.includes("text") || type.includes("txt")) return "📃";
    if (type.includes("markdown") || type.includes("md")) return "📋";

    return "📄";
  }

  validateFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      // PDF files
      "application/pdf",

      // Word documents
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx

      // Text files
      "text/plain", // .txt

      // Markdown files (multiple MIME types for compatibility)
      "text/markdown", // .md
      "text/x-markdown", // .md (alternative)
      "application/x-markdown", // .md (alternative)

      // RTF files
      "application/rtf",

      // OpenDocument Text
      "application/vnd.oasis.opendocument.text", // .odt
    ];

    // Check file size
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size exceeds 500MB limit. Current size: ${this.formatFileSize(
          file.size
        )}`,
      };
    }

    // Get file extension for additional validation
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'md', 'markdown', 'rtf', 'odt'];

    console.log(`📄 Frontend file validation - Name: ${file.name}, MIME: ${file.type}, Extension: ${fileExtension}`);

    // Check both MIME type and file extension for better compatibility
    const isMimeTypeAllowed = allowedTypes.includes(file.type);
    const isExtensionAllowed = allowedExtensions.includes(fileExtension || '');

    if (!isMimeTypeAllowed && !isExtensionAllowed) {
      return {
        isValid: false,
        error: `File type not supported. Supported formats: PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT. Received: ${file.type} (${fileExtension})`,
      };
    }

    console.log(`✅ Frontend file validation passed: ${file.name}`);
    return { isValid: true };
  }

  generateSessionTitle(firstMessage: string): string {
    // Generate a title from the first message (max 50 chars)
    const title = firstMessage.trim().slice(0, 50);
    return title.length < firstMessage.trim().length ? `${title}...` : title;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.makeRequest<{ status: string; timestamp: string }>(
      ENDPOINTS.HEALTH
    );
  }

  // Get document summary using LangGraph analysis
  async getDocumentSummary(documentId: string): Promise<{
    content: string;
    metadata: any;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to get document summary. Please log in."
      );
    }

    return this.makeRequest<{
      content: string;
      metadata: any;
    }>(`/api/knowledge/summary/${documentId}?userId=${userId}`, {
      method: "GET",
    });
  }
}

export const knowledgeService = new KnowledgeService();
